import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { useRaidContext } from '../../context/RaidContext';

import GeneralDecisionsFilters from './GeneralDecisionsFilters';
import GeneralDecisionsTable from './GeneralDecisionsTable';
import GeneralDecisionUpsertModal from './GeneralDecisionUpsertModal';

import Button from 'CommonComponents/Button';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';

export const UPSERT_DECISION_MODAL_ID = 'upsert-general-decision-modal';

export default function GeneralDecisionsIndex({ handleCreated }) {
    const [selectedDecision, setSelectedDecision] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const {
        currentVenue,
        isMasterSource,
        risks,
        issues,
        decisions,
        impactCategories,
        statuses,
        venuesOptions,
        canManageDecisions,
        fetchGeneralDecisions,
        routes,
        getQuery,
        modalsVisibility,
        toggleModalVisibility,
        loadingDatasets,
    } = useRaidContext();

    const openDecisions = useMemo(() => decisions.filter((decision) => !decision.is_closed), [decisions]);
    const closedDecisions = useMemo(() => decisions.filter((decision) => decision.is_closed), [decisions]);

    const decisionModal = useModal();

    const confirm = useConfirmationModal();

    useEffect(() => {
        setIsLoading(true);
        fetchGeneralDecisions()
            .catch((e) => {
                console.error(e);
            })
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        const currentId = getQuery().type === 'decision' ? getQuery().id : null;
        if (currentId) {
            const decision = decisions.find((d) => d.id === parseInt(currentId));
            if (decision) {
                setSelectedDecision(decision);
                toggleModalVisibility(UPSERT_DECISION_MODAL_ID, true);
            }
        }
    }, [decisions]);

    useEffect(() => {
        if (modalsVisibility[UPSERT_DECISION_MODAL_ID]) {
            decisionModal.show();
        } else {
            decisionModal.hide();
        }
    }, [modalsVisibility[UPSERT_DECISION_MODAL_ID]]);

    const pushUrlToOriginal = () => {
        const query = getQuery();
        if (query.type && history.pushState) {
            const newurl = window.location.href.split('?')[0];
            window.history.pushState({ path: newurl }, '', newurl);
        }
    };

    const onCreated = () => {
        pushUrlToOriginal();
        fetchGeneralDecisions();

        if (handleCreated && typeof handleCreated === 'function') {
            handleCreated();
        }
    };

    const onClose = () => {
        toggleModalVisibility(UPSERT_DECISION_MODAL_ID, false);
    };

    const onEdit = (model) => {
        if (!canManageDecisions) {
            return;
        }
        setSelectedDecision(model);
        toggleModalVisibility(UPSERT_DECISION_MODAL_ID, true);
    };

    const deleteDecision = (model) => {
        const deleteUrl = routes.deleteGeneralDecisionUrl.replace('GENERAL_DECISION_ID', model.id);

        axios
            .delete(deleteUrl)
            .then((res) => {
                fetchGeneralDecisions();
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const onDelete = (model) => {
        if (!canManageDecisions) {
            return;
        }

        confirm({
            title: $t('Delete Decision'),
            message: $t('Are you sure you want to delete this?'),
            onConfirm: () => {
                deleteDecision(model);
            },
        });
    };

    const onStar = (model) => {
        if (!canManageDecisions) {
            return;
        }
        const url = routes.updateGeneralDecisionHotStatusUrl.replace('GENERAL_DECISION_ID', model.id);
        axios.put(url, { is_hot: !model.is_hot }).then(() => fetchGeneralDecisions());
    };

    return (
        <Fragment>
            <div className="table__card tw-space-y-8">
                <div>
                    <div className="tw-flex tw-items-start tw-justify-between tw-mb-6">
                        <div>
                            <h4 className="table__header">{$t('Decisions')}</h4>
                        </div>
                        <div className="tw-flex tw-gap-x-1.5">
                            <Button variant="primary" as="a" href={routes.generalRisksExportUrl}>
                                {$t('Download')}
                            </Button>
                            <GeneralDecisionsFilters
                                datasets={{ venuesOptions }}
                                showSourceFilter={isMasterSource}
                                onSubmit={(values) => fetchGeneralDecisions(values)}
                                onReset={() => fetchGeneralDecisions()}
                            />
                        </div>
                    </div>
                    <GeneralDecisionsTable
                        showSourceColumn={isMasterSource}
                        isLoading={isLoading}
                        decisions={openDecisions}
                        impactCategories={impactCategories}
                        showUrl={routes.fetchGeneralDecisionUrl}
                        reorderUrl={routes.reorderGeneralDecisionUrl}
                        onStar={(decision) => onStar(decision)}
                        onEdit={(decision) => onEdit(decision)}
                        onDelete={(decision) => onDelete(decision)}
                        canManage={canManageDecisions}
                    />
                </div>
                <div>
                    <h4 className="table__header tw-mb-6">{$t('Closed Decisions')}</h4>
                    <GeneralDecisionsTable
                        showSourceColumn={isMasterSource}
                        isLoading={isLoading}
                        decisions={closedDecisions}
                        impactCategories={impactCategories}
                        showUrl={routes.fetchGeneralDecisionUrl}
                        reorderUrl={routes.reorderGeneralDecisionUrl}
                        onStar={(decision) => onStar(decision)}
                        onEdit={(decision) => onEdit(decision)}
                        onDelete={(decision) => onDelete(decision)}
                        canManage={canManageDecisions}
                    />
                </div>
            </div>

            <GeneralDecisionUpsertModal
                id={UPSERT_DECISION_MODAL_ID}
                currentVenue={currentVenue}
                isLoading={loadingDatasets}
                allowVenueSelection={isMasterSource}
                datasets={{ statuses, impactCategories, risks, issues, venuesOptions }}
                storeUrl={routes.storeGeneralDecisionUrl}
                updateUrl={routes.updateGeneralDecisionUrl}
                modal={decisionModal}
                decision={selectedDecision}
                onCreated={() => onCreated()}
                onClose={() => onClose()}
                canManage={canManageDecisions}
            />
        </Fragment>
    );
}
